// Set an hourly AND daily charge-out rate (T&M) for every machine, all on one screen —
// no opening each one. "Seed suggested rates" fills the blanks with a starting estimate
// by equipment type; you then go down the list and correct them. A manual edit saves the
// moment you leave the box; seeded numbers stay yellow (unsaved) until you Save them.
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

const money = n => (n == null || n === '') ? '' : Number(n).toLocaleString('en-US');
const round5 = n => Math.round(n / 5) * 5;

// Starting DAY rates (charge-out) by the words in a machine's name/make/model. These
// are market ballparks to get you moving — not gospel — so every one is editable and
// flagged until you confirm it. Most specific match wins; hourly ≈ day ÷ 8.
const DAY_BY_KEYWORD = [
  [/(crane|derrick|boom truck|bucket truck)/, 1200],
  [/(hydrovac|vac(uum)? (truck|trailer)|vactor)/, 700],
  [/(dozer|bulldozer)/, 900],
  [/(315|330|excavator)/, 900],
  [/(305|308|mini ?ex|compact excavator)/, 600],
  [/(wheel loader|966|950|front loader)/, 750],
  [/(backhoe|430|416)/, 500],
  [/(skid ?steer|bobcat|track loader|compact track)/, 450],
  [/(telehandler|reach ?fork|forklift|lull)/, 400],
  [/(trencher|ditch witch)/, 500],
  [/(water truck)/, 500],
  [/(dump truck|10 ?wheel|super ?dump)/, 600],
  [/(roller|compactor|bomag|padfoot|smooth drum)/, 450],
  [/(plate compactor|jumping jack|rammer)/, 120],
  [/(air ?compressor|airman|compressor)/, 300],
  [/(light ?tower|light plant)/, 150],
  [/(generator|genset)/, 200],
  [/(welder|welding)/, 150],
  [/(reel trailer|reel)/, 120],
  [/(dump trailer)/, 150],
  [/(flat ?bed trailer|flatbed|gooseneck|lowboy|utility trailer|box trailer|trailer)/, 100],
  [/(service truck|mechanic truck|flatbed truck)/, 250],
  [/(pickup|f-?150|f-?250|f-?350|1500|2500|3500|silverado|sierra|ram)/, 150],
];
const CAT_DAY_FALLBACK = { 'Construction Equipment': 500, 'CDL Trucks': 500, 'Trucks': 200, 'Trailers': 100, 'Specialty Tools': 100 };

function suggestDay(it) {
  const hay = `${it.name || ''} ${it.make || ''} ${it.model || ''}`.toLowerCase();
  for (const [re, day] of DAY_BY_KEYWORD) if (re.test(hay)) return day;
  return CAT_DAY_FALLBACK[it.category] ?? 300;
}

export default function EquipmentRates({ items, onBack, onSaved }) {
  // Local editable copy: { id: { hourly_rate, daily_rate, suggested } }
  const [edits, setEdits] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [msg, setMsg] = useState('');
  // Which Job Rate Sheet line each machine bills as on a Rate Sheet work order (Ryan,
  // Sep 29 2026 — the Hawthorne bucket truck and service truck). Truck + Equipment rows.
  const [sheetRows, setSheetRows] = useState([]);
  const [sheetPick, setSheetPick] = useState({});   // id → rate_sheet_item_id ('' = none)
  useEffect(() => { apiFetch('/rate-sheet').then(d => setSheetRows((d?.items || []).filter(i => i.category !== 'Labor'))).catch(() => setSheetRows([])); }, []);
  const sheetOf = it => (it.id in sheetPick ? sheetPick[it.id] : (it.rate_sheet_item_id || ''));
  async function saveSheet(it, v) {
    setSheetPick(p => ({ ...p, [it.id]: v }));
    try { await apiFetch(`/equipment/${it.id}`, { method: 'PATCH', body: JSON.stringify({ rate_sheet_item_id: v || null }) }); it.rate_sheet_item_id = v || null; setMsg('Saved.'); }
    catch (e) { setMsg(e?.error || 'That did not save.'); }
  }

  const val = (it, field) => {
    const e = edits[it.id];
    return e && field in e ? e[field] : (it[field] ?? '');
  };
  const setVal = (id, field, v, suggested = false) =>
    setEdits(p => ({ ...p, [id]: { ...(p[id] || {}), [field]: v, ...(suggested ? { suggested: true } : {}) } }));
  const isSuggested = id => !!edits[id]?.suggested;

  async function saveOne(it) {
    const hourly_rate = val(it, 'hourly_rate');
    const daily_rate = val(it, 'daily_rate');
    // Nothing pending for this row.
    if (String(hourly_rate) === String(it.hourly_rate ?? '') && String(daily_rate) === String(it.daily_rate ?? '')) {
      setEdits(p => { const n = { ...p }; if (n[it.id]) delete n[it.id].suggested; return n; });
      return;
    }
    try {
      const saved = await apiFetch(`/equipment/${it.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hourly_rate: hourly_rate === '' ? null : Number(hourly_rate), daily_rate: daily_rate === '' ? null : Number(daily_rate) }),
      });
      it.hourly_rate = saved.hourly_rate; it.daily_rate = saved.daily_rate; it.billing_rate = saved.billing_rate; it.rate_unit = saved.rate_unit;
      setEdits(p => { const n = { ...p }; delete n[it.id]; return n; });
    } catch (e) { setMsg('Could not save ' + (it.asset_number || it.name) + ': ' + (e?.error || 'try again')); }
  }

  function seed() {
    let filled = 0;
    setEdits(prev => {
      const next = { ...prev };
      items.forEach(it => {
        const h = (it.id in next && 'hourly_rate' in next[it.id]) ? next[it.id].hourly_rate : it.hourly_rate;
        const d = (it.id in next && 'daily_rate' in next[it.id]) ? next[it.id].daily_rate : it.daily_rate;
        if ((h == null || h === '') && (d == null || d === '')) {   // only fill fully-blank rows
          const day = suggestDay(it);
          next[it.id] = { ...(next[it.id] || {}), daily_rate: day, hourly_rate: round5(day / 8), suggested: true };
          filled++;
        }
      });
      return next;
    });
    setMsg(filled ? `Filled ${filled} blank machine${filled === 1 ? '' : 's'} with a starting estimate — review, adjust, then Save.` : 'Every machine already has a rate.');
  }

  async function saveSuggested() {
    const ids = Object.keys(edits).filter(id => edits[id]?.suggested);
    if (!ids.length) return;
    setSavingAll(true); setMsg('');
    for (const id of ids) { const it = items.find(x => x.id === id); if (it) await saveOne(it); }
    setSavingAll(false); setMsg('Saved.');
    if (onSaved) onSaved();
  }

  const suggestedCount = Object.keys(edits).filter(id => edits[id]?.suggested).length;
  const byCat = useMemo(() => {
    const g = {};
    (items || []).forEach(it => { (g[it.category || 'Other'] = g[it.category || 'Other'] || []).push(it); });
    Object.values(g).forEach(list => list.sort((a, b) => String(a.asset_number || a.name).localeCompare(String(b.asset_number || b.name))));
    return g;
  }, [items]);
  const withRate = (items || []).filter(it => (val(it, 'hourly_rate') !== '' && val(it, 'hourly_rate') != null) || (val(it, 'daily_rate') !== '' && val(it, 'daily_rate') != null)).length;

  const inp = suggested => ({
    width: 78, textAlign: 'right', border: `1px solid ${suggested ? '#f59e0b' : '#e5e7eb'}`, borderRadius: 7,
    padding: '6px 8px', fontSize: 13, background: suggested ? '#fffbeb' : '#fff', fontFamily: 'inherit',
  });

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6 }}>‹ Equipment</button>
          <h1 className="admin-page-title">💵 Equipment Rates</h1>
          <p className="admin-page-sub">Charge-out (T&amp;M) rates · {withRate} of {items.length} priced. A change saves when you leave the box. <b>On the Rate Sheet</b> says which Job Rate Sheet line the machine bills as when a work order is priced by the Rate Sheet (Bucket Truck, Excavation Truck…); left blank, the obvious ones are matched by name and the rest bill at the rates here.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <button onClick={seed}
          style={{ padding: '9px 16px', borderRadius: 9, border: '1.5px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          ✨ Seed suggested rates
        </button>
        {suggestedCount > 0 && (
          <button onClick={saveSuggested} disabled={savingAll}
            style={{ padding: '9px 16px', borderRadius: 9, border: 'none', background: '#15803d', color: '#fff', fontSize: 13, fontWeight: 800, cursor: savingAll ? 'default' : 'pointer', opacity: savingAll ? 0.6 : 1 }}>
            {savingAll ? 'Saving…' : `Save ${suggestedCount} suggested`}
          </button>
        )}
        {msg && <span style={{ fontSize: 12.5, color: '#6b7280' }}>{msg}</span>}
      </div>

      {Object.keys(byCat).sort().map(cat => (
        <div key={cat} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4, margin: '0 0 8px' }}>{cat} <span style={{ color: '#9ca3af' }}>({byCat[cat].length})</span></div>
          <div style={{ border: '1px solid #eef0f2', borderRadius: 12, overflow: 'hidden' }}>
            {byCat[cat].map((it, i) => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: i % 2 ? '#fafbfc' : '#fff', borderTop: i ? '1px solid #f1f3f5' : 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {it.asset_number ? <span style={{ color: '#6b7280', fontFamily: 'monospace', fontWeight: 800, marginRight: 6 }}>{it.asset_number}</span> : null}
                    {it.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{[it.year, it.make, it.model].filter(Boolean).join(' ')}</div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280' }}>
                  <span>$</span>
                  <input type="number" step="1" min="0" placeholder="—" style={inp(isSuggested(it.id))}
                    value={val(it, 'hourly_rate')}
                    onChange={e => setVal(it.id, 'hourly_rate', e.target.value)}
                    onBlur={() => saveOne(it)} />
                  <span style={{ fontSize: 11 }}>/hr</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280' }}>
                  <span>$</span>
                  <input type="number" step="1" min="0" placeholder="—" style={inp(isSuggested(it.id))}
                    value={val(it, 'daily_rate')}
                    onChange={e => setVal(it.id, 'daily_rate', e.target.value)}
                    onBlur={() => saveOne(it)} />
                  <span style={{ fontSize: 11 }}>/day</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280' }} title="Bills as this Job Rate Sheet line on a Rate Sheet work order">
                  <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>On the Rate Sheet</span>
                  <select value={sheetOf(it)} onChange={e => saveSheet(it, e.target.value)}
                    style={{ width: 170, border: `1px solid ${sheetOf(it) ? '#86efac' : '#e5e7eb'}`, borderRadius: 7, padding: '6px 8px', fontSize: 12.5, background: sheetOf(it) ? '#f0fdf4' : '#fff', fontFamily: 'inherit', color: '#111' }}>
                    <option value="">— not set —</option>
                    {sheetRows.map(r => <option key={r.id} value={r.id}>{r.description}{r.rate != null ? ` · $${money(r.rate)}/${String(r.unit || 'hr').toLowerCase()}` : ''}</option>)}
                  </select>
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
